/**
 * Free-shipping threshold (gross order value) shared between the PDP and the cart (ORT-3090).
 *
 * Source of truth is valantic's /pdp-data (shippingMethods price tiers, derived by
 * deriveFreeShippingThreshold). The PDP persists the value here when it loads; the cart's
 * ShippingProgressBar reads it, falling back to the historical 50 EUR when no /pdp-data value
 * has been seen this session (e.g. a cart page opened without first visiting a PDP).
 *
 * Interim FE-only carrier via sessionStorage; a server-embedded value (like baseProductPrices)
 * would remove the cross-page dependency and the DE/AT keying caveat.
 */
import { fetchPdpData, getCountryParamFromUrl, isCustomerLoggedIn } from '../shopware/pdpData';

const KEY = 'orthomol_free_shipping_threshold';
export const DEFAULT_FREE_SHIPPING_THRESHOLD = 50;

export function setFreeShippingThreshold(value: number | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    // Clear on a missing/invalid value so a threshold from a previous PDP can't linger for the
    // rest of the session (e.g. PDP A stores 60, PDP B has no free-shipping tier). getters then
    // fall back to the default instead of showing a stale, unrelated threshold.
    if (value == null || !Number.isFinite(value)) {
      window.sessionStorage.removeItem(KEY);
      return;
    }
    window.sessionStorage.setItem(KEY, String(value));
  } catch {
    // sessionStorage unavailable (private mode, quota) — cart falls back to the default.
  }
}

export function getFreeShippingThreshold(fallback: number = DEFAULT_FREE_SHIPPING_THRESHOLD): number {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const value = raw != null ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

/** Minimal shape of a Shopware cart line item we need to find a product number. */
interface LineItemLike {
  type?: string;
  payload?: unknown;
}

/**
 * Refresh the cached threshold from /pdp-data for the CURRENT (guest/customer) context.
 *
 * Why the cart needs this: the threshold is otherwise only written on PDP loads and right after
 * login(). Any path that reaches the cart without those, already-logged-in navigation, the
 * once-per-session login() short-circuit, or a deep link straight to /shop/cart, would keep
 * showing the stale value cached earlier this session. That is why the correct limit appears
 * "sometimes" (when a PDP/login happened to run in the right context) and stale otherwise.
 *
 * The free-shipping tiers come back at the cart level, so any one product number in the cart
 * yields the correct threshold. Best-effort: never throw into the caller. Returns true when a
 * /pdp-data response was applied, false when it was skipped or failed, so callers can decide
 * whether to retry rather than treating a failed call as done. (ORT-3109/ORT-3090)
 */
export async function refreshFreeShippingThreshold(
  lineItems: LineItemLike[] | undefined | null,
  opts?: { withCustomerToken?: boolean },
): Promise<boolean> {
  try {
    const productNumber = (lineItems ?? [])
      .filter((li) => li.type === 'product')
      .map((li) => (li.payload as { productNumber?: unknown } | undefined)?.productNumber)
      .find((pn): pn is string => typeof pn === 'string' && pn.length > 0);
    if (!productNumber) return false;

    const withCustomerToken = opts?.withCustomerToken ?? isCustomerLoggedIn();
    const data = await fetchPdpData([productNumber], getCountryParamFromUrl(), { withCustomerToken });
    const item = data[productNumber];
    if (!item) return false;
    setFreeShippingThreshold(item.freeShippingThreshold);
    return true;
  } catch (error) {
    console.warn('[freeShipping] Could not refresh free-shipping threshold:', error);
    return false;
  }
}
